import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, RefreshCw } from "lucide-react";

const ADMIN_EMAIL = "admin@colleague.app";

function Stat({ label, value, sublabel, target, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white rounded-2xl border border-[#E6E2D8] p-5"
    >
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#9A938A]">
        {label}
      </p>
      <div className="mt-2 font-serif text-3xl text-[#2D2926] tracking-tight">
        {value}
      </div>
      {sublabel && (
        <p className="mt-1 text-xs text-[#6B655D]">{sublabel}</p>
      )}
      {target && (
        <p className="mt-1 text-[10px] uppercase tracking-wider text-[#D4A574]">
          Target: {target}
        </p>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function userStatus(u) {
  const now = new Date();
  const paidUntil = u.access_expires_at ? new Date(u.access_expires_at) : null;
  const trialEnds = u.trial_ends_at ? new Date(u.trial_ends_at) : null;
  if (paidUntil && paidUntil > now) return "Colleague+";
  if (trialEnds && trialEnds > now) return "Trial";
  return "Expired";
}

export default function Admin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    api
      .get("/admin/metrics")
      .then(({ data }) => {
        setData(data);
        setErr(null);
      })
      .catch((e) => {
        setErr(e?.response?.status === 403 ? "Admins only." : "Couldn't load metrics.");
      })
      .finally(() => setLoading(false));
  }, [reloadKey]);

  if (!user) return null;
  if (user.email?.toLowerCase() !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen bg-[#FBF9F6]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <p className="text-sm text-[#6B655D]">
            This page is admin-only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#FBF9F6]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-start justify-between gap-3 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#9A938A] mb-2">
              Founder dashboard
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2926] tracking-tight">
              The numbers
            </h1>
            {data?.as_of && (
              <p className="mt-2 text-xs text-[#9A938A]">
                As of {new Date(data.as_of).toLocaleString()}
              </p>
            )}
          </div>
          <button
            type="button"
            data-testid="admin-refresh-btn"
            onClick={() => setReloadKey((k) => k + 1)}
            className="text-xs text-[#6B655D] hover:text-[#2D2926] rounded-full border border-[#E6E2D8] px-3 py-1.5 flex items-center gap-1.5 transition-colors"
          >
            <RefreshCw size={12} strokeWidth={1.5} /> Refresh
          </button>
        </div>

        {loading && (
          <p className="text-sm text-[#9A938A]">Loading…</p>
        )}
        {err && (
          <p className="text-sm text-red-600">{err}</p>
        )}

        {data && (
          <>
            {/* Top row — the founder-critical numbers */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Stat
                label="MRR"
                testid="stat-mrr"
                value={`$${data.revenue.mrr_usd.toFixed(2)}`}
                sublabel="Recurring monthly"
                target="$100 by day 30"
              />
              <Stat
                label="Total users"
                testid="stat-total-users"
                value={data.users.total}
                sublabel={`+${data.users.signups_30d} in last 30 days`}
              />
              <Stat
                label="Trial→Paid %"
                testid="stat-conversion"
                value={`${data.revenue.conversion_30d_pct}%`}
                sublabel="last 30d signups → paid"
                target="4–8%"
              />
              <Stat
                label="Day-7 retention"
                testid="stat-retention"
                value={
                  data.retention.d7_retention_pct == null
                    ? "—"
                    : `${data.retention.d7_retention_pct}%`
                }
                sublabel="of users 7–14 days ago"
                target="35–50%"
              />
            </div>

            {/* Subscriber breakdown */}
            <h2 className="font-serif text-2xl text-[#2D2926] mb-4 flex items-center gap-2">
              <Sparkles size={16} strokeWidth={1.5} className="text-[#D4A574]" />
              User base
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
              <Stat
                label="In free trial"
                testid="stat-trial"
                value={data.users.in_trial}
                sublabel="active trials right now"
              />
              <Stat
                label="Colleague+"
                testid="stat-paid"
                value={data.users.paid_active}
                sublabel="paid users active"
              />
              <Stat
                label="Expired"
                testid="stat-expired"
                value={data.users.expired}
                sublabel="lapsed, data preserved"
              />
            </div>

            {/* Engagement */}
            <h2 className="font-serif text-2xl text-[#2D2926] mb-4 flex items-center gap-2">
              <Sparkles size={16} strokeWidth={1.5} className="text-[#D4A574]" />
              Engagement
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <Stat
                label="Weekly active users"
                testid="stat-wau"
                value={data.engagement.weekly_active_users}
                sublabel="sent ≥1 message in 7d"
              />
              <Stat
                label="Msgs (7d)"
                testid="stat-msgs"
                value={data.engagement.messages_last_7d}
                sublabel="user messages this week"
              />
              <Stat
                label="Msgs / active user"
                testid="stat-msgs-per-user"
                value={data.engagement.avg_msgs_per_active_user}
                sublabel="weekly average"
                target="3–5"
              />
              <Stat
                label="Profile completion"
                testid="stat-profile-pct"
                value={`${data.engagement.profile_completion_pct}%`}
                sublabel="of users finished welcome"
                target="60–75%"
              />
            </div>

            {/* Recent signups */}
            <h2 className="font-serif text-2xl text-[#2D2926] mb-4 flex items-center gap-2">
              <Sparkles size={16} strokeWidth={1.5} className="text-[#D4A574]" />
              Last 10 signups
            </h2>
            <div
              data-testid="recent-signups"
              className="bg-white rounded-2xl border border-[#E6E2D8] overflow-hidden"
            >
              <table className="w-full text-sm">
                <thead className="bg-[#F2EFE9]">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B655D]">
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3">Provider</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E6E2D8]">
                  {data.recent_signups.map((u) => (
                    <tr key={u.user_id} className="text-[#3D3833]">
                      <td className="px-4 py-3 text-xs">{u.email}</td>
                      <td className="px-4 py-3 text-xs">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="px-4 py-3 text-xs capitalize">
                        {u.auth_provider}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
                            userStatus(u) === "Colleague+"
                              ? "bg-[#FBF6EE] text-[#C99560]"
                              : userStatus(u) === "Trial"
                              ? "bg-[#F2EFE9] text-[#6B655D]"
                              : "bg-[#FCEEEE] text-[#A04040]"
                          }`}
                        >
                          {userStatus(u)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
